import React, { createContext, useContext, useEffect, useMemo, useReducer } from "react";

// Shopping cart for the online ordering page.
// - Lines are keyed by UPC (falls back to id). An item with variations
//   (Roll / Wedge / Hero) gets one line per selected variation: the key is
//   "<upc|id>::<encoded lower-case label>", so different variations never
//   merge and the same variation always does.
// - All money math is done in integer cents to avoid floating-point errors.
// - The cart is saved per store in localStorage (key: ezout_cart:<customerId>).

const CartContext = createContext(null);
const MAX_QTY = 99;

export const toCents = (amount) => Math.round((Number(amount) || 0) * 100);
export const formatMoney = (cents) =>
  `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const itemBaseKey = (item) => String(item.upc || item.id);
const normalizeLabel = (label) => String(label ?? "").trim().toLowerCase();
export const lineKey = (item, variationLabel) => {
  const base = itemBaseKey(item);
  const label = normalizeLabel(variationLabel);
  return label ? `${base}::${encodeURIComponent(label)}` : base;
};

const storageKey = (customerId) => `ezout_cart:${customerId}`;

function loadCart(customerId) {
  try {
    const raw = localStorage.getItem(storageKey(customerId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((l) => l && l.key && l.qty > 0) : [];
  } catch {
    return [];
  }
}

function saveCart(customerId, lines) {
  try {
    localStorage.setItem(storageKey(customerId), JSON.stringify(lines));
  } catch {
    // Storage full or blocked (private mode) — the cart still works in memory.
  }
}

const clampQty = (qty) => Math.max(0, Math.min(MAX_QTY, Math.floor(Number(qty) || 0)));

export function cartReducer(lines, action) {
  switch (action.type) {
    case "ADD": {
      const { item, variation } = action;
      const addQty = clampQty(action.qty ?? 1) || 1;
      const variationLabel = variation ? String(variation.label ?? "").trim() : "";
      const key = lineKey(item, variationLabel);
      const existing = lines.find((l) => l.key === key);
      if (existing) {
        return lines.map((l) => (l.key === key ? { ...l, qty: clampQty(l.qty + addQty) } : l));
      }
      return [
        ...lines,
        {
          key,
          id: item.id,
          upc: item.upc,
          itemKey: itemBaseKey(item),
          ...(variationLabel ? { variationLabel } : {}),
          description: item.description,
          // The selected variation's own price, never the item's base price.
          priceCents: toCents(variation ? variation.price : item.price),
          taxRate: Number(item.taxRate) || 0,
          image_urls: item.image_urls || (item.image_url ? [item.image_url] : []),
          qty: addQty,
        },
      ];
    }
    case "SET_QTY": {
      const qty = clampQty(action.qty);
      if (qty === 0) return lines.filter((l) => l.key !== action.key);
      return lines.map((l) => (l.key === action.key ? { ...l, qty } : l));
    }
    case "INCREMENT":
      return lines.map((l) => (l.key === action.key ? { ...l, qty: clampQty(l.qty + 1) } : l));
    case "DECREMENT":
      return lines
        .map((l) => (l.key === action.key ? { ...l, qty: l.qty - 1 } : l))
        .filter((l) => l.qty > 0);
    case "REMOVE":
      return lines.filter((l) => l.key !== action.key);
    case "CLEAR":
      return [];
    default:
      return lines;
  }
}

// Keyed by customerId so switching stores remounts the store with that
// store's saved cart (and never writes one store's cart under another's key).
export function CartProvider({ customerId, children }) {
  return (
    <CartStore key={customerId} customerId={customerId}>
      {children}
    </CartStore>
  );
}

function CartStore({ customerId, children }) {
  const [lines, dispatch] = useReducer(cartReducer, customerId, loadCart);

  useEffect(() => {
    saveCart(customerId, lines);
  }, [customerId, lines]);

  const value = useMemo(() => {
    const subtotalCents = lines.reduce((sum, l) => sum + l.priceCents * l.qty, 0);
    const taxCents = lines.reduce((sum, l) => sum + Math.round(l.priceCents * l.qty * l.taxRate), 0);
    const itemCount = lines.reduce((sum, l) => sum + l.qty, 0);

    return {
      lines,
      itemCount,
      subtotalCents,
      taxCents,
      totalCents: subtotalCents + taxCents,
      getQty: (item, variationLabel) =>
        lines.find((l) => l.key === lineKey(item, variationLabel))?.qty || 0,
      // Total across every variation of one parent item (for the item card).
      getItemQty: (item) =>
        lines
          .filter((l) => (l.itemKey || l.key) === itemBaseKey(item))
          .reduce((sum, l) => sum + l.qty, 0),
      addItem: (item, { variation, qty } = {}) => dispatch({ type: "ADD", item, variation, qty }),
      increment: (key) => dispatch({ type: "INCREMENT", key }),
      decrement: (key) => dispatch({ type: "DECREMENT", key }),
      setQty: (key, qty) => dispatch({ type: "SET_QTY", key, qty }),
      removeItem: (key) => dispatch({ type: "REMOVE", key }),
      clearCart: () => dispatch({ type: "CLEAR" }),
    };
  }, [lines]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
